// @flow
import * as React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Box } from 'grommet';
import { withRouter } from 'react-router';
import { withLeaflet } from 'react-leaflet';
import { isEmpty } from 'lodash';
import { withConfirmation } from 'speed-js-react';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import type { Map } from 'esm-web-pro/containers/map/types';
import type { Cable } from 'esm-web-pro/types/networks';
import ContentLabel from 'esm-web-pro/components/layout/sidePanel/ContentLabel';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import Fiber from './CableFiber';

type Props = {
    cable: Cable,
    connectedFibers: Array<Fiber>,
    damagedFibers: Array<Fiber>,
    freeFibers: Array<Fiber>,
    editor: BaseEditor,
    map: Map,
    reload: () => void,
    highlightFiber: Function,
    clicked: string,
    setClicked: Function,
    reload: Function,
};

export type VisualisedFiber = {
    id: number,
    path: {
        [number | string]: Array<[number, number]>
    },
    container: {
        start_container_point: {
            sequence: number,
            id: number,
            coordinates: {
                type: string,
                coordinates: [number, number]
            }
        },
        end_container_point: {
            sequence: number,
            id: number,
            coordinates: {
                type: string,
                coordinates: [number, number]
            }
        },
        id: number,
        start_termination_point: ?{
            sequence: number,
            id: number,
        },
        end_termination_point: ?{
            sequence: number,
            id: number,
        },
        start_sequence: number,
        end_sequence: number,
    }
};

const FiberList = (props: Props) => {
    const {
        cable, connectedFibers, damagedFibers, editor, freeFibers, highlightFiber, clicked, setClicked,
        map, reload,
    } = props;

    const getFibers = (fibers, damaged = false) => (
        <Box pad={{ bottom: 'small' }}>
            {fibers.map((fiber) => (
                <Fiber
                    key={fiber.uuid}
                    fiber={fiber}
                    cable={cable}
                    damaged={damaged}
                    map={map}
                    highlightFiber={highlightFiber}
                    editor={editor}
                    clicked={clicked}
                    setClicked={setClicked}
                    reload={reload}
                />
            ))}
        </Box>
    );

    return (
        <Box pad={{ vertical: '10px' }} height={{ min: '100%' }}>
            <ContentLabel>
                <FormattedMessage
                    id="fiber-type-available"
                    defaultMessage="Available"
                />
            </ContentLabel>
            {(isEmpty(freeFibers)) && (
                <Box pad={{ horizontal: 'medium' }}>
                    <ValueText>
                        <FormattedMessage
                            id="point12-no-content-disconnected"
                            defaultMessage="No disconnected fibers"
                        />
                    </ValueText>
                </Box>
            )}
            {getFibers(freeFibers)}
            <ContentLabel>
                <FormattedMessage
                    id="point12-content-connected"
                    defaultMessage="Connected"
                />
            </ContentLabel>
            {(isEmpty(connectedFibers)) && (
                <Box pad={{ horizontal: 'medium' }}>
                    <ValueText>
                        <FormattedMessage
                            id="point12-no-content-connected"
                            defaultMessage="No connected fibers"
                        />
                    </ValueText>
                </Box>
            )}
            {getFibers(connectedFibers)}
            <ContentLabel>
                <FormattedMessage
                    id="point12-content-damaged"
                    defaultMessage="Damaged"
                />
            </ContentLabel>
            {(isEmpty(damagedFibers)) && (
                <Box pad={{ horizontal: 'medium' }}>
                    <ValueText>
                        <FormattedMessage
                            id="point12-no-content-damaged"
                            defaultMessage="No damaged fibers"
                        />
                    </ValueText>
                </Box>
            )}
            {getFibers(damagedFibers, true)}
        </Box>
    );
};

export default withConfirmation(withLeaflet(injectIntl(withRouter((FiberList)))));
